// assistantDock.js — WHERE THE ASSISTANT'S ANSWERS GO (ASSISTANT_BOARD_MODE_SPEC
// §1; WS-W2-ASSISTANT-PAGE-MODE-DOCK-1, umbrella DESIGN-WL-ASSISTANT-MAIN-MODE-1;
// Nika 2026-09-27 "go all recommended").
//
// ONE state key, `root.assistantDock`:
//   'right' | 'left'  SIDEBAR — the flat docked drawer on that edge
//   'minimized'       MINIMIZED — the bottom pill
//   'main'            PAGE — the main thread over the home's grid area (the
//                     DEFAULT, Nika's pick 6 A; AI_ASSISTANT_SPEC v1 §1), never
//                     in the drawer; nothing reserves a column. A placement:
//                     it needs no board service — only the Dashboard MODE does
// The UI speaks in three ANSWER MODES — Page · Sidebar · Minimized — so the
// sidebar's SIDE is its own small memory (the last edge the user docked to).
//
// PERSISTED PER USER with the theme mode's discipline (shared themeMode.js):
// localStorage paints first (`symbols_assistant_dock`, `symbols_assistant_side`);
// the `user_preferences` row (`assistantDock`) follows the user to every
// device. The record wins once it has loaded; a local choice with no record
// migrates up — only a CHOICE: a person's pick marks itself on the device
// (`symbols_assistant_dock_explicit`), and an unmarked stored value is an old
// default the shell once kept (before Page became the default) that neither
// paints nor copies up (Nika 2026-09-30: on a second device the sidebar won
// over the empty record and would have spread to every device). With no
// record and no marked choice the placement is the default, 'main' — never
// written up. The prefs `update` REPLACES the row, so every write obeys the
// home-prefs durability law (functions/homeLook.js; setAssistantMode): single-
// flight on the queue every writer of the row shares, the payload composed
// from the LIVE row at send time, an unloaded row READ first, the newest
// decision last — and never a partial copy of the row in the state.
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained — the few literals are restated.

// setAssistantDock(value) via el.call — or setAssistantDock(root, value, sdk)
// from shell code. Writes the state key, remembers the side, persists the
// choice locally and to the user record. An explicit choice also ends a
// session promotion (see promoteAssistantDockForOpen). Returns the record
// write (a promise; null on failure) when an SDK is at hand.
// The shell form takes `{ recordOnly: true }` fourth: the boot migrate, where
// the state and this device already hold the choice — only the record write
// runs (a live session promotion stays untouched).
export function setAssistantDock(a, b, c, d) {
  const docks = ['right', 'left', 'minimized', 'main']
  const viaCall = typeof a === 'string' || a == null
  const value = viaCall ? a : b
  const self = this && typeof this === 'object' ? this : null
  const root = viaCall ? (self && self.state && self.state.root) || null : a
  const sdk = viaCall
    ? (self && (self.sdk || (self.context && self.context.sdk))) || null
    : c || null
  const recordOnly = !viaCall && !!(d && d.recordOnly)
  if (!docks.includes(value)) {
    // AI-308 — a silent ignore cost a live-test cycle once (a caller passed
    // 'docked' and nothing moved, with no signal why).
    console.warn(
      `[shell] setAssistantDock ignored invalid value "${String(value)}" — ` +
        "accepted values: 'left' | 'right' | 'minimized' | 'main'"
    )
    return
  }
  if (!root || typeof root.update !== 'function') return
  // The loaded copy follows at once — ONLY when the row has loaded. A partial
  // `{ assistantDock }` copy of an unloaded row would read as the whole row to
  // the next writer, which sends it and erases every other stored preference
  // (the data-loss defect: a cold boot, or the window after a workspace switch
  // wipes `userPrefs`).
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  if (!recordOnly) {
    const patch = { assistantDock: value, _assistantDockPromotedFrom: null }
    if (prefs && prefs.assistantDock !== value) patch.userPrefs = { ...prefs, assistantDock: value }
    root.update(patch, { preventFetch: true })
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('symbols_assistant_dock', value)
        // the MARK: a person chose this (readStoredAssistantDock reads only
        // marked values; the boot migrate copies only them up)
        localStorage.setItem('symbols_assistant_dock_explicit', '1')
        if (value === 'left' || value === 'right')
          localStorage.setItem('symbols_assistant_side', value)
      }
    } catch (err) {
      console.warn('[shell] assistant-dock persist failed:', err && err.message)
    }
  }
  // Every explicit choice is SENT, the active value included: the loaded copy
  // can be stale (a boot read that predates an earlier write lands after it),
  // so "the copy already says so" proves nothing about the record. The write
  // is idempotent; a burst collapses to its newest decision below.
  if (!sdk || typeof sdk.execute !== 'function') return
  // The queue every user_preferences writer shares, keyed as they key it.
  const w = root.workspace
  const key =
    String(
      (w && (w.id || w._id)) || (root.jwtClaims && root.jwtClaims.active_workspace_id) || ''
    ) || 'user'
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  root.__assistantDockSeq = (root.__assistantDockSeq || 0) + 1
  const seq = root.__assistantDockSeq
  const prior = queue.get(key) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      // A decision superseded while it waited sends nothing.
      if (seq !== root.__assistantDockSeq) return null
      // PER-PATH PATCH — names only `assistantDock`, so a second tab's write
      // to any OTHER key is never overwritten (CORE-USERPREFS-PATCH-PER-PATH-1).
      const row = await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch: { set: { assistantDock: value } }
      })
      // Adopt the confirmed row only when no newer decision waits.
      if (seq === root.__assistantDockSeq) {
        const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
        root.update(
          {
            userPrefs: now
              ? { ...now, assistantDock: value }
              : row && typeof row === 'object'
                ? row
                : { assistantDock: value }
          },
          { preventFetch: true }
        )
      }
      return row
    })
  queue.set(key, task)
  return task.catch((err) => {
    console.warn('[shell] user_preferences assistantDock save failed:', err && err.message)
    return null
  })
}

// setAssistantAnswersMode('page' | 'sidebar' | 'minimized') — the ANSWERS
// choice's writer (the kit AppSegmented calls `el.call(select, value)`):
// Page → 'main', Sidebar → the last side this device docked to, Minimized →
// 'minimized'; then the one writer above, through el.call (never a bare
// sibling read — FA204).
export function setAssistantAnswersMode(mode) {
  let side = 'right'
  try {
    const store = typeof localStorage !== 'undefined' ? localStorage : null
    if (store && store.getItem('symbols_assistant_side') === 'left') side = 'left'
  } catch (_) {
    side = 'right'
  }
  const dock = mode === 'page' ? 'main' : mode === 'minimized' ? 'minimized' : side
  if (this && typeof this.call === 'function') return this.call('setAssistantDock', dock)
}

// The last sidebar edge this device docked to — 'right' when none.
export const lastAssistantSide = (storage) => {
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const side = store ? store.getItem('symbols_assistant_side') : null
    return side === 'left' ? 'left' : 'right'
  } catch (_) {
    return 'right'
  }
}

// The stored dock on this device, or null when the user never CHOSE one: an
// unmarked value is an old default the shell once kept, never a choice.
export const readStoredAssistantDock = (storage) => {
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    if (!store || store.getItem('symbols_assistant_dock_explicit') !== '1') return null
    const v = store.getItem('symbols_assistant_dock')
    return ['right', 'left', 'minimized', 'main'].includes(v) ? v : null
  } catch (_) {
    return null
  }
}

// The answer mode a dock value shows as, and back.
export const answersModeOf = (dock) =>
  dock === 'main' ? 'page' : dock === 'minimized' ? 'minimized' : 'sidebar'
export const dockForAnswersMode = (mode, side) =>
  mode === 'page' ? 'main' : mode === 'minimized' ? 'minimized' : side === 'left' ? 'left' : 'right'

// Boot reconcile, once the user_preferences row has landed. PURE:
//   { apply: dock }    the record holds a choice that differs from the state
//   { migrate: dock }  no record choice; this device holds one — persist it up
//   null               nothing to do
export const reconcileAssistantDockFromPrefs = (currentDock, prefs, storedDock) => {
  const docks = ['right', 'left', 'minimized', 'main']
  const norm = (v) => (docks.includes(v) ? v : null)
  const recorded = norm(prefs && typeof prefs === 'object' ? prefs.assistantDock : null)
  if (recorded) return recorded !== currentDock ? { apply: recorded } : null
  const local = norm(storedDock)
  return local ? { migrate: local } : null
}

// THE STAMP — what <html data-assistant-dock> says, from the state:
//   drawer open  → its edge ('right' | 'left'), or 'minimized'
//   drawer shut  → 'main' in Page mode, else 'closed'
// The injected shell CSS keys the reserved column and the drawer on
// right / left only, so 'main' reserves nothing.
export const assistantDockStamp = (root) => {
  const r = root || {}
  const dock = r.assistantDock
  if (!r.assistantOpen) return dock === 'main' ? 'main' : 'closed'
  return dock === 'left' || dock === 'minimized' ? dock : 'right'
}

// An OPENER (the header star, a calendar "Ask AI", a ticket mention, a deep
// link, a starter) flips `assistantOpen` true: it asks for the conversation.
// In Page mode that is the MAIN THREAD over the current page (AI_ASSISTANT_SPEC
// §12.2; Nika 2026-09-29 "make main thread ai as default" — everywhere): the
// drawer stays shut and the thread's flag goes up on this route (the view shows
// an empty thread's hint until the conversation arrives). Two exceptions keep
// the SESSION promotion to the last side, remembered in
// `_assistantDockPromotedFrom` so closing the drawer returns to Page (Esc never
// changes the mode): a page SHEET open in the PageSlot or an opener inside a
// DIALOG (`opts.overlay` — a ticket or event detail "Ask AI"; both cover the
// main area, the drawer docks beside them), and an explicit "Open in
// sidebar", which writes that promotion itself (functions/
// assistantMainThread.js assistantThreadToSidebar) and so never reaches this
// branch. Minimized keeps its long-standing promotion to the right edge, not
// remembered (unchanged). Returns the patch to apply, or null.
//
// THE SITE BUILDER (/preview, also under a `/w/<org>[/<ws>]` prefix) is the
// third: the chat docks BESIDE the site it builds, like a sheet (Nika
// 2026-10-28 "like lovable"). Session promotion only — no preference is
// written, so leaving the builder leaves Page mode as it was.
export const promoteAssistantDockForOpen = (root, side, opts) => {
  const r = root || {}
  if (r.assistantDock === 'minimized') return { assistantDock: 'right' }
  if (r.assistantDock === 'main') {
    const builder =
      typeof r.currentPath === 'string' &&
      /^(?:\/w\/[a-z0-9-]{1,48}(?:\/(?!p(?:[/?#]|$))[A-Za-z0-9_-]{1,64})?)?\/preview(?:[/?#]|$)/.test(
        r.currentPath
      )
    if (r._canvasSlot || r._canvasSlotSecond || (opts && opts.overlay) || builder) {
      return {
        assistantDock: side === 'left' ? 'left' : 'right',
        _assistantDockPromotedFrom: 'main'
      }
    }
    const path = typeof r.currentPath === 'string' && r.currentPath ? r.currentPath : '/'
    return { assistantOpen: false, _homeThreadOpen: true, _mainThreadPath: path }
  }
  return null
}

// The close edge: a drawer promoted out of Page mode returns to it.
export const restoreAssistantDockOnClose = (root) => {
  const r = root || {}
  return r._assistantDockPromotedFrom === 'main'
    ? { assistantDock: 'main', _assistantDockPromotedFrom: null }
    : null
}
